/* eslint-disable no-nested-ternary */
import styled from 'styled-components'

import { ProgressBarProps, useFont } from '@confer/lib'
import { getColours } from '@confer/lib/utils'
import { Banner } from './Banner'
import { ClearheadProgressPanBanner } from './ClearheadProgressPanBanner'
import { GridLayoutProps } from './Interfaces'
import { Logos } from './Logos'
import SectionNavigation from './SectionNavigation'

const DesktopGridLayout = (props: GridLayoutProps) => {
  const { settings, question, children } = props
  const { logoSettings, leftBanner, rightBanner, backgroundImage, progressBar } = settings
  const hasBannerNavigation =
    !!leftBanner && !!props.sectionNavigation?.items.some((item) => item.parent)

  const { backgroundColour, primaryColour } = getColours(settings)

  const fontFamily = useFont()

  const logosProps =
    question.hideLogo && logoSettings
      ? {
          ...logoSettings,
          hide: question.hideLogo,
        }
      : logoSettings

  const progressProps: ProgressBarProps = {
    percentage: props.currentPercentage,
    settings,
    hide: !progressBar,
  }

  return (
    <Container fontFamily={fontFamily}>
      {leftBanner ? (
        <LeftBannerSlot>
          {settings.surveyName === 'psychosocial-hazards-clearhead' && leftBanner.progressPan ? (
            <ClearheadProgressPanBanner userProps={leftBanner} progressBarProps={progressProps} />
          ) : (
            <Banner userProps={leftBanner} progressBarProps={progressProps} />
          )}
          {hasBannerNavigation && props.sectionNavigation && (
            <BannerNavigationOverlay>
              <SectionNavigation {...props.sectionNavigation} presentation="banner" />
            </BannerNavigationOverlay>
          )}
        </LeftBannerSlot>
      ) : undefined}
      <Content background={backgroundImage} backgroundColour={backgroundColour}>
        <Header>
          {logosProps ? <Logos {...logosProps} /> : undefined}
          {props.sectionNavigation ? (
            hasBannerNavigation ? (
              <CompactNavigationFallback>
                <SectionNavigation {...props.sectionNavigation} presentation="compact" />
              </CompactNavigationFallback>
            ) : (
              <SectionNavigation {...props.sectionNavigation} />
            )
          ) : question.section ? (
            <Section colour={question.section.colour}>{question.section.name}</Section>
          ) : undefined}
          {props.headerAction ? <HeaderAction>{props.headerAction}</HeaderAction> : undefined}
        </Header>
        {children}
        {progressBar && !leftBanner && !rightBanner && (
          <InlineProgress>
            <progress
              aria-label="Survey progress"
              max={100}
              value={props.currentPercentage}
              style={{ accentColor: primaryColour }}
            />
            <span>{Math.round(props.currentPercentage)}% complete</span>
          </InlineProgress>
        )}
      </Content>
      {rightBanner ? (
        <Banner userProps={rightBanner} progressBarProps={progressProps} />
      ) : undefined}
    </Container>
  )
}

interface ContainerProps {
  fontFamily: string
}

const Container = styled.div<ContainerProps>`
  display: flex;
  width: 100vw;
  height: 100vh;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  margin: 0;
  font-family: ${(props) => props.fontFamily};
`

const LeftBannerSlot = styled.div`
  position: relative;
  flex: 0 0 30vw;
  width: 30vw;
  height: 100%;
`

const BannerNavigationOverlay = styled.div`
  display: none;
  position: absolute;
  inset: 0;
  z-index: 1;
  padding: 0.75rem;
  pointer-events: none;
  @media (min-width: 1280px) {
    display: block;
  }
`

const CompactNavigationFallback = styled.div`
  @media (min-width: 1280px) {
    display: none;
  }
`

interface ContentProps {
  background?: string
  backgroundColour: string
}

const Content = styled.div<ContentProps>`
  display: flex;
  flex: 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  margin: 0;
  background-image: ${(props) => (props.background ? `url(${props.background})` : 'none')};
  background: ${(props) => props.backgroundColour};
`
const InlineProgress = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 2rem 1rem;
  font-size: 0.875rem;

  progress {
    flex: 1;
    min-width: 0;
    height: 0.75rem;
  }

  span {
    white-space: nowrap;
  }
`

const Header = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
`

const HeaderAction = styled.div`
  display: flex;
  align-self: flex-end;
  margin-right: 1rem;
  margin-top: 0.5rem;
`

interface SectionProps {
  colour?: string
}

const Section = styled.div<SectionProps>`
  padding-left: 1rem;
  font-size: 1.1rem;
  // color: ${(props) => props.colour || 'black'};
  // font-weight: 600;
  &::after {
    content: '';
    position: relative;
    width: 100%;
    height: 1.5px;
    left: 0;
    display: block;
    clear: both;
    // background-color: rgba(0, 0, 0, 0.5)
    background-color: ${(props) => props.colour || 'rgba(0, 0, 0, 0.5)'};
  }
`

export default DesktopGridLayout
